import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { distActions } from '../store';
import { fetchWrapper } from '../helpers';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../context/organizationContext';

const baseUrl = `${process.env.REACT_APP_API_URL}`;

export const useModeratorPost = (employeeId, organizationName) => {
	const dispatch = useDispatch();

	const { t } = useTranslation();
	const employee = useSelector((state) =>
		state.dist.selected_employees.find((e) => e.id === employeeId),
	);
	const moderatorPost = useSelector((state) => state.dist.moderatorPosts[employeeId] || {});
	const tempImages = useSelector((state) => state.dist.tempImages[employeeId] || []);
	const tempVideo = useSelector((state) => state.dist.tempVideos[employeeId]);
	const isGenerating = useSelector((state) => state.dist.generating);
	const formState = useSelector((state) => state.dist.form);
	const jobStatus = useSelector((state) => state.dist.jobStatus?.[employeeId]);

	// Insert organization name into the hashtags field
	const makeOrganizationHashtag = (orgName) => {
		// Remove anything except English letters, convert to lowercase
		const cleanOrg = orgName.replace(/[^a-zA-Z]/g, '').toLowerCase();

		// Add hashtag and return
		return '#' + cleanOrg;
	};

	const organization = useOrganization();
	const orgNameHashtag = makeOrganizationHashtag(organization.organization.name) || '';

	// Initialize post from job result
	const initializePost = (jobResult) => {
		if (jobResult && !moderatorPost.initialized) {
			const parsed = jobResult || {};
			dispatch(
				distActions.setModeratorPost({
					employeeId,
					postData: {
						post:
							parsed?.result?.post ||
							'Write your own post, or generate it with the form. If you see this, please regenerate the post',
						postTitle: parsed?.result?.postTitle || 'New post',
						hashtags: `#empoweredbydiffizio ${orgNameHashtag}`,
						media: parsed?.result?.media,
						initialized: true,
						published: false,
						publishTime: new Date().toISOString(),
						weekDay: '',
						socialNetwork: [],
						sent: false,
					},
				}),
			);
		}
	};

	// Update post content
	const updatePostContent = (updates) => {
		dispatch(
			distActions.setModeratorPost({
				employeeId,
				postData: updates,
			}),
		);
	};

	// Handle image operations
	// IMPORTANT: images and video are mutually exclusive for a single distributed post
	// - If a video is already chosen, block adding images (media_type constraint)
	const addImages = (files) => {
		if (tempVideo) {
			message.error('You already selected a video. Remove the video to upload images.');
			return;
		}
		const list = Array.from(files).filter((f) => f && f.type?.startsWith('image/'));
		if (!list.length) {
			message.warning('No valid image files were selected.');
			return;
		}
		// Respect your cap of 6 images
		const room = Math.max(0, 10 - (tempImages?.length || 0));
		if (room <= 0) {
			message.info('Maximum 10 images per post.');
			return;
		}
		list.slice(0, room).forEach((image) => {
			// Allow typical image types only, avoid odd blobs
			if (!/^image\/(png|jpe?g|webp|gif|bmp|heic|heif)$/i.test(image.type)) {
				message.warning(`Unsupported image type: ${image.type || 'unknown'}`);
				return;
			}
			dispatch(distActions.addTempImage({ employeeId, image }));
		});
	};

	const removeImage = (index) => {
		dispatch(distActions.removeTempImage({ employeeId, index }));
	};

	const clearImages = () => {
		dispatch(distActions.clearTempImages({ employeeId }));
	};

	// Handle video operations
	// IMPORTANT: images and video are mutually exclusive.
	// We accept one (1) video only; common social limits are large, but we’ll soft-limit to 200 MB.
	const setVideo = (video) => {
		if (!video) return;
		if (tempImages?.length) {
			message.error('Images already selected. Remove images to attach a video.');
			return;
		}
		const isVideo = /^video\//i.test(video.type);
		if (!isVideo) {
			message.error('Selected file is not a video.');
			return;
		}
		// Basic type allow-list for safety (covers your accept attribute set)
		const ok =
			/(mp4|quicktime|x-ms-wmv|x-msvideo|x-flv|mpeg|webm|3gpp)$/i.test(video.type) ||
			/\.(mp4|mov|wmv|avi|flv|mpeg|mpg|webm|3gp)$/i.test(video.name || '');
		if (!ok) {
			message.warning('This video format may not be supported by target platforms.');
		}
		// Soft limit ~200 MB; keep UX consistent with your UI hint
		const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
		if (video.size > MAX_VIDEO_BYTES) {
			message.error('Video is too large (max ~200 MB).');
			return;
		}
		dispatch(distActions.setTempVideo({ employeeId, video }));
	};

	const clearVideo = () => {
		dispatch(distActions.clearTempVideo({ employeeId }));
	};

	const pollJob = async (jobId, { interval = 1500, max = 120, signal } = {}) => {
		let attempts = 0;
		while (attempts < max) {
			if (signal?.aborted) throw new Error('Aborted');
			const res = await fetchWrapper.get(`${baseUrl}/dist/job/${jobId}`);
			const backendState = res?.state;
			const normalized =
				backendState === 'completed'
					? 'completed'
					: backendState === 'failed'
					? 'failed'
					: backendState === 'delayed'
					? 'delayed'
					: 'queued'; // waiting/active → queued

			// keep Redux status fresh for UI overlays/icons
			dispatch(
				distActions.setJobStatus({
					employeeId,
					jobId,
					status: normalized,
					result: res?.result,
					error: res?.error,
				}),
			);

			if (normalized === 'completed') return res;
			if (normalized === 'failed') {
				const e = new Error(res?.error || 'Job failed');
				e.__jobFailed = true;
				throw e;
			}

			await new Promise((r) => setTimeout(r, interval));
			attempts++;
		}
		throw new Error('Polling timed out');
	};

	const regeneratePost = async (customPrompt = null) => {
		const ac = new AbortController();
		try {
			// 1) If previous run failed — clean slate
			if (jobStatus?.status === 'failed' || jobStatus?.error) {
				dispatch(distActions.clearModeratorPost({ employeeId }));
				if (distActions.clearJobStatus) {
					dispatch(distActions.clearJobStatus({ employeeId }));
				} else {
					// fallback
					dispatch(
						distActions.setJobStatus({
							employeeId,
							jobId: null,
							status: null,
							result: null,
							error: null,
						}),
					);
				}
			}

			// 2) Start a new regen job
			const content = formState.contentType === 0 ? formState.postLink : formState.postText;
			const start = await fetchWrapper.post(`${baseUrl}/dist/regenerate`, {
				employeeId, // backend accepts employeeId or userId
				title: formState.title,
				article: content,
				instruction: customPrompt ?? formState.instruction ?? '',
				contentType: formState.contentType === 0 ? 'link' : 'text',
				languages: formState.languages,
				originalPost: moderatorPost.post,
			});

			const jobId = start?.jobId;
			if (!jobId) throw new Error('No jobId returned');

			dispatch(
				distActions.setJobStatus({
					employeeId,
					jobId,
					status: 'queued',
					result: null,
					error: null,
				}),
			);

			// 3) Poll to completion
			const done = await pollJob(jobId, { signal: ac.signal });

			// 4) Replace content in Redux
			let payload = done?.result;
			// some processors return stringified JSON
			if (typeof payload === 'string') {
				try {
					payload = JSON.parse(payload);
				} catch (_) {}
			}
			const r = payload?.result ?? payload ?? {};

			dispatch(
				distActions.setModeratorPost({
					employeeId,
					postData: {
						post:
							r?.post ||
							'Write your own post, or generate it with the form. If you see this, please regenerate the post',
						postTitle: r?.postTitle || 'New post',
						hashtags: r?.hashtags || `#empoweredbydiffizio ${orgNameHashtag}`,
						media: r?.media || [],
						initialized: true,
						sent: false,
						publishTime: moderatorPost?.publishTime || new Date().toISOString(), // keep existing schedule
					},
				}),
			);
			const regenerationSuccessMessage = t('postDistribution.regenerationSuccess');
			message.success(regenerationSuccessMessage);
		} catch (err) {
			dispatch(
				distActions.setJobStatus({
					employeeId,
					jobId: jobStatus?.jobId || null,
					status: 'failed',
					result: null,
					error: err?.message || 'Regeneration failed',
				}),
			);
			const regenerationFailureMessage = t('postDistribution.regenerationFailure');
			message.error(regenerationFailureMessage);
			console.error(err);
		} finally {
			dispatch(distActions.setGenerating(false));
		}

		return () => ac.abort();
	};

	// Get user social media accounts
	const connectedPlatforms = useSelector(
		(state) => state.dist.employeeSocialMediaMap?.[employeeId] || [],
	);
	
	const replaceImage = useCallback((index, newImageFile) => {
		distActions.setTempImages((prev) => {
			const updated = [...prev];
			updated[index] = newImageFile;
			return updated;
		});
	}, []);

	// Submit final post (this is where API calls happen)
	const submitPost = async (autopublication = false, instagramContentType) => {
		try {
			dispatch(distActions.setGenerating(true));

			// Enforce media exclusivity before building payload
			if (tempVideo && (tempImages?.length || 0) > 0) {
				message.error('Please keep EITHER images OR a video for a single post (not both).');
				throw new Error('Invalid media combination');
			}

			// Prepare form data for submission
			const formData = new FormData();

			// Add post data
			formData.append('employeeId', employeeId);
			formData.append('post', moderatorPost.post || '');
			formData.append('postTitle', moderatorPost.postTitle);
			formData.append('hashtags', moderatorPost.hashtags || '');
			formData.append('media', JSON.stringify(moderatorPost.media || []));
			formData.append('publishTime', moderatorPost.publishTime || new Date().toISOString());
			formData.append(
				'socialNetwork',
				Array.isArray(moderatorPost.socialNetwork)
					? moderatorPost.socialNetwork.join(',')
					: moderatorPost.socialNetwork || '',
			);
			formData.append('autopublication', autopublication);
			formData.append('instagramContentType', instagramContentType);

			// Add images (if any)
			if (!tempVideo && tempImages?.length) {
				tempImages.forEach((image) => {
					formData.append('images', image);
				});
			}

			// Upload video first, then add its URL to the payload
			if (tempVideo) {
				const videoForm = new FormData();
				videoForm.append('video', tempVideo, tempVideo.name);
				// IMPORTANT: fetchWrapper must NOT force JSON headers for FormData bodies
				var videoUrl = null;
				const uploadResponse = await fetchWrapper.post(`${baseUrl}/dist/upload-video`, videoForm, {
					raw: true,
				});
				const responseData = await uploadResponse.json();

				if (!responseData.success || !responseData.url) {
					throw new Error('Video upload failed');
				} else {
					videoUrl = responseData.url;
				}
				formData.append('videoUrl', videoUrl);
			}

			// Do not send new posts to employees yet...
			const apiUrl = `${process.env.REACT_APP_API_URL}/dist/send/`;
			const response = await fetchWrapper.post(apiUrl, formData, { raw: true });
			console.log(response);

			if (response?.status === 200 || response.status === 201) {
				const submissionSuccessMessage = t('postDistribution.submissionSuccess');
				message.success(submissionSuccessMessage);

				// Clear the moderator post data after successful submission
				dispatch(
					distActions.setModeratorPost({
						employeeId,
						postData: { sent: true }, // set up the flag
					}),
				);

				return response;
			} else {
				const submissionErrorMessage = t('postDistribution.submissionFailure');
				throw new Error(submissionErrorMessage);
			}
		} catch (error) {
			const submissionErrorMessage = t('postDistribution.submissionFailure');
			message.error(submissionErrorMessage);
			console.error(error);
			throw error;
		} finally {
			dispatch(distActions.setGenerating(false));
		}
	};

	// Get current post data with fallbacks
	const getCurrentPost = () => ({
		post: moderatorPost.post || 'Write your own post, or generate it using the form.',
		postTitle: moderatorPost.postTitle || 'New post - please write it yourself or generate it.',
		hashtags: moderatorPost.hashtags || `#empoweredbydiffizio ${orgNameHashtag}`,
		media: moderatorPost.media || [],
		publishTime: moderatorPost.publishTime || new Date().toISOString(),
		weekDay: moderatorPost.weekDay || '',
		socialNetwork: moderatorPost.socialNetwork || [],
		sent: moderatorPost.sent || false,
		published: false,
	});

	// Validate post data
	const validatePost = () => {
		const errors = [];
		const currentPost = getCurrentPost();

		if (!currentPost.post || currentPost.post.trim().length < 25) {
			const errorMessage = t('postDistribution.characterMinLimitError');
			errors.push(errorMessage);
		}

		if (currentPost.post.length > 2000) {
			const errorMessage = t('postDistribution.characerMaxLimitError');
			errors.push(errorMessage);
		}

		if (!currentPost.hashtags || currentPost.hashtags.trim().length === 0) {
			const errorMessage = t('postDistribution.hashtagsRequired');
			errors.push(errorMessage);
		}

		// Media validation (exclusive + size budgets)
		if (tempVideo && (tempImages?.length || 0) > 0) {
			errors.push('Keep either images OR a video, not both.');
		}
		if (tempVideo) {
			const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
			if (tempVideo.size > MAX_VIDEO_BYTES) {
				errors.push('Video exceeds the ~200 MB size limit.');
			}
		} else {
			const totalImagesSize = tempImages.reduce((size, image) => size + (image?.size || 0), 0);
			if (totalImagesSize > 10_000_000) {
				const errorMessage = t('postDistribution.fileSizeLimitExceeded');
				errors.push(errorMessage || 'Total images size must be < 10 MB.');
			}
		}

		return {
			isValid: errors.length === 0,
			errors,
		};
	};

	// Clear all data for this employee
	const clearPost = () => {
		dispatch(distActions.clearModeratorPost({ employeeId }));
	};

	const toggleSocialNetwork = (platform) => {
		const current = Array.isArray(moderatorPost.socialNetwork) ? moderatorPost.socialNetwork : [];
		const updated = current.includes(platform)
			? current.filter((p) => p !== platform)
			: [...new Set([...current, platform])]; // гарантируем уникальность

		dispatch(
			distActions.setModeratorPost({
				employeeId,
				postData: { socialNetwork: updated },
			}),
		);
	};

	return {
		// Data
		employee,
		moderatorPost,
		tempImages,
		tempVideo,
		isGenerating,
		currentPost: getCurrentPost(),

		// Actions
		initializePost,
		updatePostContent,
		addImages,
		removeImage,
		clearImages,
		setVideo,
		clearVideo,
		regeneratePost,
		submitPost,
		validatePost,
		clearPost,
		toggleSocialNetwork,
	};
};
